import React, { createContext, useContext, useState, useEffect } from 'react';
import { formatPrice, getCurrencySymbol } from '../utils/currency';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    // Return default values if used outside provider
    return {
      currency: 'usd',
      setCurrency: () => {},
      setStoreId: () => {},
      formatPrice: (amount) => formatPrice(amount, 'usd'),
      getCurrencySymbol: () => getCurrencySymbol('usd'),
      isLoading: false,
    };
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('usd');
  const [storeId, setStoreId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [storeCurrencyCache, setStoreCurrencyCache] = useState({});

  // Fetch currency for a specific store
  const fetchStoreCurrency = async (storeIdOrSlug) => {
    if (!storeIdOrSlug) {
      setCurrency('usd');
      return;
    }

    // Check cache first
    if (storeCurrencyCache[storeIdOrSlug]) {
      setCurrency(storeCurrencyCache[storeIdOrSlug]);
      return;
    }

    try {
      setIsLoading(true);
      
      // Try to fetch store info from backend
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stores?active_only=false`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('plateos_token')}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const stores = data.stores || [];
        
        // Find store by ID or slug (normalized name)
        const normalizeStoreName = (name) => {
          if (!name) return '';
          return name.replace(/[^a-z0-9]/gi, '').toLowerCase();
        };
        
        const normalizedSlug = normalizeStoreName(storeIdOrSlug);
        const store = stores.find(
          s => s.id === storeIdOrSlug || normalizeStoreName(s.name) === normalizedSlug
        );

        if (store && store.currency) {
          const newCurrency = store.currency.toLowerCase();
          setCurrency(newCurrency);
          
          // Cache the result
          setStoreCurrencyCache(prev => ({
            ...prev,
            [storeIdOrSlug]: newCurrency,
            [store.id]: newCurrency,
          }));
        } else {
          setCurrency('usd'); // fallback
        }
      }
    } catch (error) {
      console.error('Failed to fetch store currency:', error);
      setCurrency('usd'); // fallback on error
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-fetch currency when storeId changes
  useEffect(() => {
    if (storeId) {
      fetchStoreCurrency(storeId);
    }
  }, [storeId]);

  const value = {
    currency,
    setCurrency,
    storeId,
    setStoreId, // Components can call this to set the current store context
    formatPrice: (amount, decimals = 2) => formatPrice(amount, currency, decimals),
    formatPriceWithCurrency: (amount, currencyCode, decimals = 2) => formatPrice(amount, currencyCode || currency, decimals),
    getCurrencySymbol: (overrideCurrency) => getCurrencySymbol(overrideCurrency || currency),
    isLoading,
    fetchStoreCurrency, // Expose for manual calls
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
